<template>
  <div id="app" data-app class="v-application--is-ltr">
    <!-- Loading Overlay -->
    <v-overlay :value="authInitializing || appLoading" absolute>
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>

    <!-- Main App -->
    <MainLayout v-if="isAuthenticated && !authInitializing && !$route.meta.requiresGuest && !$route.meta.hideLayout" />
    <router-view v-else-if="!authInitializing" />

    <!-- Global Confirmation Dialog -->
    <ConfirmationDialog
      :show="confirmationDialog.show"
      :message="confirmationDialog.message"
      :title="confirmationDialog.title"
      @confirm="handleConfirm"
      @cancel="handleCancel"
    />

    <!-- Notification Permission Dialog -->
    <NotificationPermission />

    <!-- PWA Update Notification (production only; dev skips deploy-style checks) -->
    <PWAUpdateNotification v-if="enablePwaUpdateUi" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ConfirmationDialog from "./components/ConfirmationDialog.vue";
import NotificationPermission from "./components/NotificationPermission.vue";
import PWAUpdateNotification from "./components/PWAUpdateNotification.vue";
import serviceWorkerUpdateService from "./services/serviceWorkerUpdate";

export default {
  name: "App",
  components: {
    // Lazy-loaded so its heavy deps (socket.io, etc.) stay out of the initial/login bundle
    MainLayout: () => import("./components/layout/MainLayout.vue"),
    ConfirmationDialog,
    NotificationPermission,
    PWAUpdateNotification,
  },
  data() {
    return {
      authInitializing: true,
    };
  },
  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters(["appLoading", "confirmationDialog"]),
    enablePwaUpdateUi() {
      return process.env.NODE_ENV === "production";
    },
  },
  methods: {
    ...mapActions("auth", ["initializeAuth"]),
    ...mapActions("notifications", ["initializePushNotifications", "resyncPushToken"]),
    ...mapActions(["confirmDialog", "cancelDialog"]),
    handleConfirm() {
      this.confirmDialog();
    },
    handleCancel() {
      this.cancelDialog();
    },
    handlePushResumeSync() {
      if (!this.isAuthenticated) return;
      this.resyncPushToken().catch((error) => {
        console.warn("Push token resume sync failed:", error);
      });
    },
    onPushVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.handlePushResumeSync();
      }
    },
    onPushPageShow(event) {
      // pageshow with persisted = bfcache restore on Safari
      if (event.persisted || document.visibilityState === "visible") {
        this.handlePushResumeSync();
      }
    },
  },
  async created() {
    // Initialize authentication on app start
    try {
      // Use the returned auth result instead of relying on getter
      const authResult = await this.initializeAuth();

      // Never block UI boot on push — SW/FCM can hang; clear overlay after auth
      if (authResult && authResult.authenticated) {
        this.initializePushNotifications().catch((pushError) => {
          console.error("Push notification initialization failed:", pushError);
        });
      }

      if (process.env.NODE_ENV === "production") {
        serviceWorkerUpdateService.initialize();
      }
    } catch (error) {
      console.error("App initialization failed:", error);
      // Ensure app can still start even if auth fails
    } finally {
      this.authInitializing = false;
    }

    // iOS PWA: re-sync FCM token when returning from background
    document.addEventListener("visibilitychange", this.onPushVisibilityChange);
    window.addEventListener("pageshow", this.onPushPageShow);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onPushVisibilityChange);
    window.removeEventListener("pageshow", this.onPushPageShow);
  },
  watch: {
    isAuthenticated(newVal) {
      // Initialize push notifications when user logs in
      if (newVal) {
        this.initializePushNotifications().catch((error) => {
          console.error("Push notification init failed:", error);
        });
      }
    },
  },
};
</script>

<style>
#app {
  font-family: var(--font-ui);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Global styles */
.v-application {
  font-family: var(--font-ui) !important;
}

/* Custom scrollbar (dark-first) */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.28);
}

/* Removed global transitions to allow Vuetify default behavior */

/* Global Date Picker Styling - Consistent across all date pickers */
.v-application .v-picker,
.v-application .v-picker__body,
.v-application .v-date-picker {
  background-color: #1e1e1e !important;
  color: white !important;
}

.v-application .v-picker__title {
  background-color: #000000 !important;
  color: white !important;
}

.v-application .v-date-picker-header {
  background-color: #000000 !important;
  color: white !important;
  padding: 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.v-application .v-date-picker-header *,
.v-application .v-date-picker-header button,
.v-application .v-date-picker-header .v-btn,
.v-application .v-date-picker-header .v-btn__content,
.v-application .v-date-picker-header .v-icon,
.v-application .v-date-picker-header span {
  color: white !important;
}

.v-application .v-date-picker-header .v-date-picker-header__value {
  color: white !important;
}

.v-application .v-date-picker-header .v-date-picker-header__value button,
.v-application .v-date-picker-header .v-date-picker-header__value .v-btn {
  color: white !important;
}

.v-application .v-date-picker-table {
  background-color: #1e1e1e !important;
}

.v-application .v-date-picker-table th {
  color: rgba(255, 255, 255, 0.8) !important;
  font-weight: 600 !important;
  padding: 8px !important;
}

.v-application .v-date-picker-table .v-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  min-width: 40px !important;
  height: 40px !important;
}

.v-application .v-date-picker-table .v-btn .v-btn__content {
  color: rgba(255, 255, 255, 0.9) !important;
}

.v-application .v-date-picker-table .v-btn--active {
  background-color: var(--brand-purple) !important;
  color: white !important;
}

.v-application .v-date-picker-table .v-btn--active .v-btn__content,
.v-application .v-date-picker-table .v-btn--active *,
.v-application .v-date-picker-table .v-btn--active span {
  color: white !important;
  font-weight: 600 !important;
}

.v-application .v-date-picker-table .v-btn--outlined {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.v-application .v-date-picker-table .v-btn:hover:not(.v-btn--active) {
  background-color: rgba(96, 88, 200, 0.2) !important;
}

.v-application .v-date-picker-table .v-btn--disabled,
.v-application .v-date-picker-table .v-btn--disabled .v-btn__content {
  color: rgba(255, 255, 255, 0.3) !important;
}

/* Vuetify detached menus: v-select, v-autocomplete, v-combobox, v-overflow-btn, v-menu */
.v-menu__content {
  background: var(--brand-panel) !important;
  /* backdrop-filter: blur(10px) !important; TEMPORARILY DISABLED TO FIX MODAL BLUR */
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35) !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

.v-menu__content .v-list,
.v-menu__content .v-select-list {
  background: transparent !important;
}

.v-menu__content .v-list {
  padding: 0.5rem 0 !important;
}

.v-menu__content .v-select-list .v-list {
  padding: 0 !important;
}

.v-menu__content .v-list-item {
  background: transparent !important;
  color: rgba(255, 255, 255, 0.9) !important;
  min-height: 48px !important;
}

.v-menu__content .v-list-item:hover {
  background: rgba(255, 255, 255, 0.06) !important;
}

.v-menu__content .v-list-item--active {
  background: rgba(96, 88, 200, 0.22) !important;
  color: #fff !important;
}

.v-menu__content .v-list-item--active .v-list-item__title,
.v-menu__content .v-list-item--active .v-list-item__subtitle {
  color: #fff !important;
}

.v-menu__content .v-list-item__content {
  color: inherit !important;
}

.v-menu__content .v-list-item__title {
  color: rgba(255, 255, 255, 0.92) !important;
}

.v-menu__content .v-list-item__subtitle {
  color: rgba(255, 255, 255, 0.55) !important;
}

.v-menu__content .v-list-item--disabled {
  opacity: 0.45 !important;
}

.v-menu__content .v-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Default menu icons on dark menus; preserve explicit semantic colors */
.v-menu__content:not(.theme--light) .v-list-item__icon .v-icon:not(.error--text):not(.success--text):not(.warning--text):not(.info--text):not(.primary--text):not(.secondary--text):not(.accent--text):not(.grey--text) {
  color: rgba(255, 255, 255, 0.9) !important;
}

/* Multi-select menu checkboxes (v-select / v-autocomplete / v-combobox) */
.v-menu__content .v-simple-checkbox .v-icon {
  color: #6058c8 !important;
}

.v-menu__content .v-list-item:hover .v-simple-checkbox .v-icon {
  color: #9f98e8 !important;
}

.v-menu__content .v-list-item--active .v-simple-checkbox .v-icon {
  color: #c4bfff !important;
}

/* Hide mobile-only back buttons on desktop; detail headers use .header-nav-return */
@media (min-width: 960px) {
  #app .back-btn:not(.header-nav-return),
  .main-navigation-drawer .back-btn,
  .main-navigation-drawer .v-btn[class*="back"]:not(.header-nav-return) {
    display: none !important;
  }

  #app .page-header .header-nav-return,
  #app .page-header .app-header-back-btn.header-nav-return {
    display: inline-flex !important;
  }
}

/* Filled inputs: error messages in red (only when in error state) */
.theme--dark.v-input--has-state.error--text .v-messages__message {
  color: #ff5252 !important;
}

/* Filled inputs: space between prepend-inner icon and text (exclude outlined — breaks notch alignment) */
.theme--dark .v-text-field:not(.v-text-field--outlined) .v-input__prepend-inner {
  margin-right: 8px !important;
}

/* Solo-inverted: keep dark bg on focus instead of flipping to white */
.theme--dark.v-text-field--solo-inverted.v-input--is-focused
  > .v-input__control
  > .v-input__slot {
  background: rgba(255, 255, 255, 0.08) !important;
}
.theme--dark.v-text-field--solo-inverted.v-input--is-focused input,
.theme--dark.v-text-field--solo-inverted.v-input--is-focused .v-icon,
.theme--dark.v-text-field--solo-inverted.v-input--is-focused .v-label {
  color: rgba(255, 255, 255, 0.87) !important;
}
.theme--dark.v-text-field--solo-inverted input::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}
.theme--dark.v-text-field--solo-inverted .v-input__prepend-inner {
  margin-right: 8px !important;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  body,
  html {
    background: var(--brand-stage) !important;
  }

  #app {
    background: var(--brand-stage);
  }
}

@media (max-width: 600px) {
  .v-container:not(.content-container) {
    padding: 8px !important;
  }

  .v-card {
    margin: 4px !important;
  }
}

/* ==========================================================================
   MODERN UI SYSTEM - Reusable across all pages
   ========================================================================== */

.primary-gradient {
  background: var(--brand-gradient-primary) !important;
  border: none !important;
  color: white !important;
}

.primary-gradient-text {
  background: var(--brand-gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.warning-gradient {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%) !important;
  border: none !important;
  color: white !important;
}

/* 1. Global page header + .page-content: client/src/styles/app-page-shell.css */

/* 2. Dashboard KPI cards: client/src/styles/app-stat-card.css (.app-stat-card via AppStatCard) */

/* 3. Table chrome lives in styles/data-table-surface.css (.data-table-surface) */

/* 4. Global Indicators & Chips */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff5252;
  box-shadow: 0 0 8px rgba(255, 82, 82, 0.4);
  display: inline-block;
}

.status-dot.active {
  background: #00e676;
  box-shadow: 0 0 8px rgba(0, 230, 118, 0.4);
}

.status-chip {
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.status-chip.verified {
  border: 1px solid rgba(0, 230, 118, 0.2) !important;
  color: #00e676 !important;
}

/* 5. User UI Components (within tables) */
.user-avatar {
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

.user-name-text {
  font-weight: 600;
  color: white;
  margin-bottom: 2px;
}

.user-id-text {
  font-size: 0.7rem;
  opacity: 0.3;
  font-family: "JetBrains Mono", monospace;
}

/* 6. Form Components */
.modern-select ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
}

/* 7. Card Title Icon Spacing - Consistent across all pages */
.card-title .v-icon {
  margin-right: 8px !important;
}

</style>
